// Pluggable drone deployment strategies.
//
// Each strategy is a pure planning function: given the current fire front,
// infrastructure, available stations, and weather, it returns a list of
// { origin, target } assignments (one drone per available station at most).
// The planner wraps assignments into missions; the Strategy Lab runs any set
// of strategies head-to-head on cloned engines with paired random seeds.

import {
  STATE_BURNING,
  STATE_SMOLDERING,
  STATE_INFRASTRUCTURE,
} from "./constants";

const dist = (a, b) => Math.hypot(a.row - b.row, a.col - b.col);

const clampCell = (engine, cell) => ({
  row: Math.max(0, Math.min(engine.rows - 1, Math.round(cell.row))),
  col: Math.max(0, Math.min(engine.cols - 1, Math.round(cell.col))),
});

// Threat assessment shared by most strategies: for each infrastructure cell,
// find the nearest fire cell and attach the cell's protection value.
const assessThreats = (fireCells, infraCells, structureValue, maxDist) => {
  const threats = [];
  for (const infra of infraCells) {
    let minFireDist = Infinity;
    let closestFire = null;
    for (const f of fireCells) {
      const d = dist(infra, f);
      if (d < minFireDist) {
        minFireDist = d;
        closestFire = f;
      }
    }
    if (minFireDist <= maxDist) {
      threats.push({
        ...infra,
        minFireDist,
        closestFire,
        value: structureValue?.[infra.row]?.[infra.col] || 1,
      });
    }
  }
  return threats;
};

// Greedily assign the nearest available station to each target
const assignStations = (targets, availableStations, maxAssignments) => {
  const stations = [...availableStations];
  const assignments = [];
  for (const target of targets) {
    if (assignments.length >= maxAssignments || stations.length === 0) break;
    let best = -1;
    let bestD = Infinity;
    stations.forEach((s, i) => {
      const d = dist(s, target);
      if (d < bestD) {
        bestD = d;
        best = i;
      }
    });
    if (best >= 0) {
      assignments.push({
        origin: { row: stations[best].row, col: stations[best].col },
        target: { row: target.row, col: target.col },
      });
      stations.splice(best, 1);
    }
  }
  return assignments;
};

// Drop N cells behind the asset (away from the fire) — defensive line
const defensiveTarget = (engine, threat, offset) => {
  const d = threat.minFireDist || 1;
  return clampCell(engine, {
    row: threat.row + ((threat.row - threat.closestFire.row) / d) * offset,
    col: threat.col + ((threat.col - threat.closestFire.col) / d) * offset,
  });
};

// Drop between the fire and the asset — interception line
const interceptTarget = (engine, threat, offset) => {
  const d = threat.minFireDist || 1;
  return clampCell(engine, {
    row: threat.row + ((threat.closestFire.row - threat.row) / d) * offset,
    col: threat.col + ((threat.closestFire.col - threat.col) / d) * offset,
  });
};

// Deduplicate targets that are within `minSpacing` cells of each other
const spaceOut = (targets, minSpacing) => {
  const kept = [];
  for (const t of targets) {
    if (!kept.some((k) => dist(k, t) < minSpacing)) kept.push(t);
  }
  return kept;
};

export const DRONE_STRATEGIES = {
  none: {
    name: "No Drones",
    short: "Baseline",
    description: "Control run — fire burns with no autonomous response.",
    plan: () => [],
  },

  nearest: {
    name: "Nearest Threat",
    short: "Nearest",
    description:
      "Defend whichever assets the fire is closest to, dropping defensive retardant behind them. Simple and reactive.",
    plan: ({ engine, stations, fireCells, infraCells, structureValue, params }) => {
      const threats = assessThreats(
        fireCells,
        infraCells,
        structureValue,
        params.protectionDistance
      ).sort((a, b) => a.minFireDist - b.minFireDist);
      const targets = spaceOut(
        threats.map((t) => defensiveTarget(engine, t, params.targetOffset)),
        4
      );
      return assignStations(targets, stations, params.maxDronesPerCycle);
    },
  },

  valueWeighted: {
    name: "Value Priority",
    short: "Value",
    description:
      "Weigh threat urgency by asset value — hospitals, substations, and schools get protected first even if lower-value assets are closer to the fire.",
    plan: ({ engine, stations, fireCells, infraCells, structureValue, params }) => {
      const threats = assessThreats(
        fireCells,
        infraCells,
        structureValue,
        params.protectionDistance + 3
      ).sort(
        (a, b) =>
          a.minFireDist / Math.sqrt(a.value) - b.minFireDist / Math.sqrt(b.value)
      );
      const targets = spaceOut(
        threats.map((t) => defensiveTarget(engine, t, params.targetOffset)),
        4
      );
      return assignStations(targets, stations, params.maxDronesPerCycle);
    },
  },

  intercept: {
    name: "Downwind Intercept",
    short: "Intercept",
    description:
      "Predictive: only defends assets that lie downwind of the fire (where it is actually heading) and drops retardant between the fire and the asset to cut the run off early.",
    plan: ({ engine, stations, fireCells, infraCells, structureValue, weather, params }) => {
      const towardRad =
        (((weather?.direction ?? 0) + 180) % 360) * (Math.PI / 180);
      const wx = Math.sin(towardRad);
      const wy = -Math.cos(towardRad);
      const threats = assessThreats(
        fireCells,
        infraCells,
        structureValue,
        params.protectionDistance + 4
      )
        .filter((t) => {
          // Keep assets that are downwind of their nearest fire cell
          const dr = t.row - t.closestFire.row;
          const dc = t.col - t.closestFire.col;
          const d = Math.hypot(dr, dc) || 1;
          return (dc / d) * wx + (dr / d) * wy > 0.25;
        })
        .sort(
          (a, b) =>
            a.minFireDist / Math.sqrt(a.value) -
            b.minFireDist / Math.sqrt(b.value)
        );
      const targets = spaceOut(
        threats.map((t) =>
          interceptTarget(engine, t, Math.min(3, t.minFireDist * 0.45))
        ),
        3
      );
      return assignStations(targets, stations, params.maxDronesPerCycle);
    },
  },

  clusterShield: {
    name: "Cluster Shield",
    short: "Cluster",
    description:
      "Finds the most valuable threatened neighborhood and concentrates the whole cycle's drops into a shield arc on its fire-facing side, rather than spreading drones thin.",
    plan: ({ engine, stations, fireCells, infraCells, structureValue, params }) => {
      const threats = assessThreats(
        fireCells,
        infraCells,
        structureValue,
        params.protectionDistance + 2
      );
      if (threats.length === 0) return [];
      // Score each threat by the total value within a 4-cell radius
      let best = null;
      let bestScore = -1;
      for (const t of threats) {
        let score = 0;
        for (const o of threats) {
          if (dist(t, o) <= 4) score += o.value;
        }
        score /= 1 + t.minFireDist * 0.15; // urgency discount
        if (score > bestScore) {
          bestScore = score;
          best = t;
        }
      }
      // Shield arc: perpendicular to the fire->cluster axis, on the fire side
      const d = best.minFireDist || 1;
      const ux = (best.closestFire.col - best.col) / d; // toward fire
      const uy = (best.closestFire.row - best.row) / d;
      const px = -uy; // perpendicular
      const py = ux;
      const standoff = Math.min(3, d * 0.5);
      const targets = [-1, 0, 1].map((k) =>
        clampCell(engine, {
          row: best.row + uy * standoff + py * k * 3,
          col: best.col + ux * standoff + px * k * 3,
        })
      );
      return assignStations(targets, stations, params.maxDronesPerCycle);
    },
  },
};

export const DEFAULT_STRATEGY_PARAMS = {
  protectionDistance: 10,
  targetOffset: 4,
  maxDronesPerCycle: 3,
};

// Collect the current fire front and standing infrastructure from an engine
export const collectPlanningCells = (engine) => ({
  fireCells: engine.collectCells([STATE_BURNING, STATE_SMOLDERING]),
  infraCells: engine.collectCells([STATE_INFRASTRUCTURE]),
});
